import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Select,
  MenuItem,
  styled,
  Tab,
  Tabs,
} from "@mui/material";
import {
  CaretDown,
  EnvelopeSimple,
  Shield,
  ShieldStar,
  User,
  X,
} from "@phosphor-icons/react";
import {
  FilledButton,
  OutlinedButton,
} from "../../ReusableComponents/Buttons/Buttons";
import { useState } from "react";

const StyledSelect = styled(Select)({
  color: "#92B4FF",
  backgroundColor: "#252A38",
  height: "32px",
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    padding: "4px 8px",
  },
  "& .MuiPaper-root": {
    backgroundColor: "#FFFFFF",
  },
});

const StyledMenuItem = styled(MenuItem)({
  display: "flex",
  alignItems: "center",
  gap: "4px",
  color: "#92B4FF",
  "&:hover": {
    backgroundColor: "#F5F5F5",
  },
});

const RoleBadge = ({ role, isAdmin }: { role: string; isAdmin: boolean }) => {
  if (isAdmin) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          bgcolor: "#92B4FF33",
          borderRadius: "16px",
          padding: "4px 8px",
          width: "fit-content",
        }}
      >
        <Shield size={16} color="#92B4FF" weight="fill" />
        <Typography variant="body1" color="#92B4FF">
          {role}
        </Typography>
      </Box>
    );
  }

  return (
    <StyledSelect
      value={role}
      size="small"
      sx={{
        borderRadius: "16px",
        bgcolor: "#92B4FF33",
      }}
      IconComponent={(props) => (
        <CaretDown {...props} size={16} color="#A3A6B7" />
      )}
      MenuProps={{
        PaperProps: {
          sx: {
            bgcolor: "#92B4FF33",
            boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
            "& .MuiMenuItem-root": {
              color: "#252A38",
            },
          },
        },
      }}
    >
      <StyledMenuItem value="Admin">
        <Shield size={16} color="#6C65D9" weight="fill" />
        Admin
      </StyledMenuItem>
      <StyledMenuItem value="Collaborator" color="#92B4FF">
        <User size={16} color="#92B4FF" weight="fill" />
        Collaborator
      </StyledMenuItem>
    </StyledSelect>
  );
};

const ManageUsersModal = ({ onClose }: { onClose: () => void }) => {
  const [value, setValue] = useState(0);
  return (
    <Box
      sx={{
        width: "1000px",
        borderRadius: "10px",
        p: 2,
        border: "0.8px solid #606479",
        bgcolor: "#1B1D28",
        boxSizing: "border-box",
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Typography variant="h3" fontWeight={500}>
          Manage Users
        </Typography>
        <IconButton onClick={onClose}>
          <X size={20} color="#A3A6B7" />
        </IconButton>
      </Box>
      <Tabs value={value} onChange={(event, newValue) => setValue(newValue)}>
        <Tab label="Collaborators" value={0} />
        <Tab label="Invited Users" value={1} />
      </Tabs>
      <Box sx={{ width: "100%" }}>
        {value === 0 && (
          <Box sx={{ width: "100%", height: "100%", mt: 2 }}>
            <Typography variant="body1" fontWeight={500}>
              Collaborators{" "}
              <span style={{ color: "#A3A6B7", marginLeft: "4px" }}>3</span>
            </Typography>
            <TableContainer
              sx={{ boxSizing: "border-box", mt: 1, borderRadius: "5px" }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        border: "1px solid #252A38",
                        color: "#F9F9F999",
                      }}
                    >
                      Name
                    </TableCell>
                    <TableCell
                      sx={{ border: "1px solid #252A38", color: "#F9F9F999" }}
                    >
                      Role
                    </TableCell>
                    <TableCell sx={{ border: "none" }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ border: "1px solid #252A38" }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: "5px",
                            bgcolor: "#6C65D9",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#F9F9F9",
                            fontSize: "12px",
                          }}
                        >
                          KR
                        </Box>
                        <Typography variant="body1" color="#F9F9F9DE">
                          Kashyap Rayas
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ border: "1px solid #252A38" }}>
                      <RoleBadge role="Admin" isAdmin={true} />
                    </TableCell>
                    <TableCell sx={{ border: "none" }}>
                      <IconButton disabled>
                        <X color="#FF3333" size={20} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ border: "1px solid #252A38" }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: "5px",
                            bgcolor: "#6C65D9",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#F9F9F9",
                            fontSize: "12px",
                          }}
                        >
                          SS
                        </Box>
                        <Typography variant="body1" color="#F9F9F9DE">
                          Siddesh Sharma
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ border: "1px solid #252A38" }}>
                      <RoleBadge role="Collaborator" isAdmin={false} />
                    </TableCell>
                    <TableCell sx={{ border: "none" }}>
                      <IconButton>
                        <X color="#FF3333" size={20} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ border: "1px solid #252A38" }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: "5px",
                            bgcolor: "#6C65D9",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#F9F9F9",
                            fontSize: "12px",
                          }}
                        >
                          SM
                        </Box>
                        <Typography variant="body1" color="#F9F9F9DE">
                          Sejal Mishra
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ border: "1px solid #252A38" }}>
                      <RoleBadge role="Collaborator" isAdmin={false} />
                    </TableCell>
                    <TableCell sx={{ border: "none" }}>
                      <IconButton>
                        <X color="#FF3333" size={20} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
        {value === 1 && (
          <Box sx={{ width: "100%", height: "100%", mt: 2 }}>
            <Typography variant="body1" fontWeight={500}>
              Invite Users
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "center",
                mt: 1,
                mb: 1,
              }}
            >
              <TextField
                placeholder="Enter email of user here"
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    color: "#F9F9F9",
                    backgroundColor: "#252A38",
                    height: "40px",
                    "& fieldset": {
                      borderColor: "#606479",
                    },
                    "&:hover fieldset": {
                      borderColor: "#6C65D9",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#6C65D9",
                    },
                  },
                }}
              />
              <OutlinedButton
                text="Invite"
                disabled={false}
                handleClick={() => {}}
                fullWidth={false}
                minWidth={100}
                // onclick={() => {}}
              />
            </Box>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", mt: 2 }}>
              <Typography variant="body2" color="#606479">
                1 user invited
              </Typography>
              <IconButton size="small">
                <CaretDown size={16} color="#606479" weight="fill" />
              </IconButton>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",

                p: 1,

                mt: 1,
              }}
            >
              <Typography variant="body1" color="#A3A6B7">
                hasibur@vigaet.com
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <IconButton size="small">
                  <EnvelopeSimple size={20} color="#A3A6B7" />
                </IconButton>
                <IconButton size="small">
                  <X size={20} color="#A3A6B7" />
                </IconButton>
              </Box>
            </Box>
          </Box>
        )}
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
          <FilledButton
            text="Save"
            disabled={false}
            handleClick={() => {}}
            fullWidth={false}
            minWidth={100}
            isLoading={false}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default ManageUsersModal;
